// Zod contracts for the apps module. Filled by M4 (C3); nothing else edits this file.
// Every request and response schema lives here (architecture/architecture.md Section 3.11).
//
//   /api/apps                      list (GET), register (POST, S8)
//   /api/apps/:appId               everything S9 shows (GET), general section (PATCH)
//   /api/apps/:appId/<section>     one route group per settings section; every write returns the AppView
//   /api/apps/:appId/install       snippet, SDK, extension, CSP entries, test-capture links
//   /api/apps/:appId/linear        the Linear team and project the app's tickets go to (PUT; poc CT-009)
//   /api/apps/:appId/masking-finds the places found on the app's pages (GET, paged; PATCH one, answering the find;
//                                  CT-028)
//   /api/extension/v1/hosts        addresses of apps with the widget on (no auth, M12)
// The widget's own contract (GET /api/widget/v1/config) is in ./widget.ts (M7).
import { z } from 'zod';
import { CODE_LISTS } from '../constants/code-lists.js';
import { MASKING_FIND_DESCRIPTION_MAX, MASKING_FIND_STATES } from '../constants/masking-finds.js';
import { APP_ROLE_CODES } from '../constants/roles.js';
import { MASKING_FIND_LIMITS } from '../masking/limits.js';
import { envelope, MAX_PAGE_SIZE, pagedEnvelope } from './common.js';
import { AppLinearTargetSchema, LinearTargetInputSchema } from './linear.js';

const Uuid = z.uuid();
const IsoDateTime = z.iso.datetime({ offset: true });
const SHORT_TEXT_MAX = 1000;
const LONG_TEXT_MAX = 20_000;
const APP_NAME_MAX = 200;
const ENVIRONMENT_CODE = /^[a-z][a-z0-9_-]{0,49}$/;
/** Masking rule targets: a CSS selector, a regular expression source or an SDK context key. */
export const MASKING_TARGET_MAX = 500;
/** Masking rules a registration can carry; more can be added in the app's settings. None are required (CT-028). */
export const MASKING_RULES_AT_REGISTRATION_MAX = 50;
/** "Found on your pages" shows 25 finds a page (module-11 4a). */
export const MASKING_FINDS_PAGE_SIZE = 25;

export const AppRoleCodeSchema = z.enum(APP_ROLE_CODES);
export const WidgetStateSchema = z.enum(CODE_LISTS.widget_state);
export const MaskingRuleKindSchema = z.enum(CODE_LISTS.masking_rule_kind);

const Name = z.string().trim().min(1).max(APP_NAME_MAX);
const Description = z.string().trim().max(LONG_TEXT_MAX);
const ShortText = z.string().trim().min(1).max(SHORT_TEXT_MAX);
/** "bookings.hpg.au", "intranet.hpg.au/leasing" or a full URL; normalised by the server (shared/addresses). */
const AddressInput = z.string().trim().min(1).max(SHORT_TEXT_MAX);
export const EnvironmentCodeSchema = z
  .string()
  .trim()
  .regex(ENVIRONMENT_CODE, 'Lower-case letters, digits, "-" and "_", starting with a letter');

// ---------------------------------------------------------------------------------------------
// Path parameters
// ---------------------------------------------------------------------------------------------

export const AppParamsSchema = z.object({ appId: Uuid });
export const AppItemParamsSchema = z.object({ appId: Uuid, id: Uuid });
export const EnvironmentParamsSchema = z.object({ appId: Uuid, envId: Uuid });
export const AddressParamsSchema = z.object({ appId: Uuid, envId: Uuid, addressId: Uuid });

// ---------------------------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------------------------

export const PersonRefSchema = z.object({ principalId: Uuid, displayName: z.string() });
export type PersonRef = z.infer<typeof PersonRefSchema>;

export const AddressViewSchema = z.object({ id: Uuid, host: z.string(), pathPrefix: z.string() });

export const EnvironmentViewSchema = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
  addresses: z.array(AddressViewSchema),
});
export type EnvironmentView = z.infer<typeof EnvironmentViewSchema>;

export const RoutingViewSchema = z.object({
  id: Uuid,
  person: PersonRefSchema,
  description: z.string(),
  updatedAt: IsoDateTime,
});

export const MaskingRuleViewSchema = z.object({
  id: Uuid,
  kind: MaskingRuleKindSchema,
  /** The selector, the custom pattern or the context key. Null when the rule uses a library pattern. */
  target: z.string().nullable(),
  pattern: z.object({ id: Uuid, code: z.string(), name: z.string() }).nullable(),
  description: z.string().nullable(),
  updatedAt: IsoDateTime,
});

/** Everything S9 shows. Every settings write answers with the whole view again. */
export const AppViewSchema = z.object({
  id: Uuid,
  name: z.string(),
  description: z.string().nullable(),
  dataClassification: z.object({ id: Uuid, code: z.string(), label: z.string() }),
  showsGuestPersonalDetails: z.boolean(),
  showsStaffPersonalDetails: z.boolean(),
  widgetState: WidgetStateSchema,
  /** Counts the masking rule changes (DM-9). It gates nothing (CT-027: there's no masking check). */
  maskingRevision: z.number().int(),
  registeredBy: PersonRefSchema,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** The caller's role in the app: `owner` (shown as "Editor") for every Editor and Admin; null otherwise. */
  myRole: AppRoleCodeSchema.nullable(),
  /** What the caller may change here, for the settings page's buttons. */
  can: z.object({
    manageSettings: z.boolean(),
    manageMasking: z.boolean(),
  }),
  environments: z.array(EnvironmentViewSchema),
  /** An Editor or Admin. Null only while the role is vacant: the Admins are then responsible (REQ-12.3). */
  fallbackOwner: PersonRefSchema.nullable(),
  routing: z.array(RoutingViewSchema),
  masking: z.object({
    rules: z.array(MaskingRuleViewSchema),
  }),
  /** The active app key (public, goes in the snippet). */
  key: z.object({ publicKey: z.string(), createdAt: IsoDateTime }).nullable(),
  /**
   * The Linear team and project the app's tickets are handed to (poc CT-009). Null: not chosen (an app registered
   * while Linear wasn't connected); its tickets aren't handed off.
   */
  linear: AppLinearTargetSchema.nullable(),
  /** Linear is connected: registration needs a target, and App settings can change it. */
  linearConnected: z.boolean(),
});
export type AppView = z.infer<typeof AppViewSchema>;
export const AppViewResponseSchema = envelope(AppViewSchema);

export const AppSummarySchema = z.object({
  id: Uuid,
  name: z.string(),
  role: AppRoleCodeSchema,
  environmentCount: z.number().int(),
  widgetState: WidgetStateSchema,
});
export type AppSummary = z.infer<typeof AppSummarySchema>;
export const AppListResponseSchema = envelope(z.object({ items: z.array(AppSummarySchema) }));

// ---------------------------------------------------------------------------------------------
// Masking rules (REQ-10.2, REQ-10.3, REQ-10.7). No test mode and no masking check (CT-027). Masking is automatic
// (CT-028), so no app needs a rule: owners add one for what the automatic hiding can't recognise.
// ---------------------------------------------------------------------------------------------

/**
 * page_element: `target` is a CSS selector. context_key: `target` is an SDK context key.
 * pattern: either `target` (a custom regular expression) or `maskingPatternId` (a library pattern).
 */
export const CreateMaskingRuleBodySchema = z
  .object({
    kind: MaskingRuleKindSchema,
    target: z.string().trim().min(1).max(MASKING_TARGET_MAX).optional(),
    maskingPatternId: Uuid.optional(),
    description: ShortText.optional(),
  })
  .refine((v) => (v.target === undefined) !== (v.maskingPatternId === undefined), {
    message: 'Give either a target or a library pattern',
  })
  .refine((v) => v.kind === 'pattern' || v.maskingPatternId === undefined, {
    message: 'Only pattern rules can use a library pattern',
    path: ['maskingPatternId'],
  });
export type CreateMaskingRuleInput = z.infer<typeof CreateMaskingRuleBodySchema>;

export const UpdateMaskingRuleBodySchema = z
  .object({
    target: z.string().trim().min(1).max(MASKING_TARGET_MAX),
    maskingPatternId: Uuid,
    description: ShortText.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field' })
  .refine((v) => !(v.target !== undefined && v.maskingPatternId !== undefined), {
    message: 'Give either a target or a library pattern',
  });
export type UpdateMaskingRuleInput = z.infer<typeof UpdateMaskingRuleBodySchema>;

// ---------------------------------------------------------------------------------------------
// Register (S8, REQ-1.1) and the general section (REQ-1.4, REQ-10.1)
// ---------------------------------------------------------------------------------------------

export const RegisterAppBodySchema = z.object({
  name: Name,
  /** What the app does; the LLM uses it for routing (REQ-6.6). */
  description: Description.optional(),
  dataClassificationId: Uuid,
  showsGuestPersonalDetails: z.boolean(),
  showsStaffPersonalDetails: z.boolean(),
  /** At least one; each unique across every app. They go to the first environment. */
  addresses: z.array(AddressInput).min(1).max(20),
  /** The first environment: Production unless named. */
  environment: z.object({ code: EnvironmentCodeSchema, name: Name }).optional(),
  /**
   * The Linear team and project the app's tickets go to (poc CT-009). Required while Linear is connected
   * (400 without it); ignored while it isn't.
   */
  linear: LinearTargetInputSchema.optional(),
  /**
   * The app's first masking rules, created in the same transaction. Optional, whatever the personal-details answers:
   * masking is automatic (CT-028), so none is required.
   */
  maskingRules: z.array(CreateMaskingRuleBodySchema).max(MASKING_RULES_AT_REGISTRATION_MAX).optional(),
});
export type RegisterAppInput = z.infer<typeof RegisterAppBodySchema>;

export const UpdateAppBodySchema = z
  .object({
    name: Name,
    description: Description.nullable(),
    dataClassificationId: Uuid,
    showsGuestPersonalDetails: z.boolean(),
    showsStaffPersonalDetails: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field' });
export type UpdateAppInput = z.infer<typeof UpdateAppBodySchema>;

// ---------------------------------------------------------------------------------------------
// Found on your pages (CT-028, REQ-10.9, module-4 2a): where the widget's automatic hiding hid something, one row per
// app and place (detector, selector, page path). Never a value. An owner allows a place (its screenshots leave it
// unmasked for that detector) or renames it.
// ---------------------------------------------------------------------------------------------

export const MaskingFindStateSchema = z.enum(MASKING_FIND_STATES);

export const MaskingFindParamsSchema = z.object({ appId: Uuid, findId: Uuid });

/** `q` searches the selector, the descriptions, the label and the page path (case-insensitive substring). */
export const MaskingFindsQuerySchema = z.object({
  q: z.string().trim().max(MASKING_FIND_LIMITS.placeChars).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(MASKING_FINDS_PAGE_SIZE),
});
export type MaskingFindsQuery = z.infer<typeof MaskingFindsQuerySchema>;

export const MaskingFindViewSchema = z.object({
  id: Uuid,
  /** `form_value`, `personal_label`, a library pattern code, a built-in detector or an app's own pattern code. */
  detector: z.string(),
  /** `input`, `textarea`, `select`, `column`, `field` or `text` (MASKING_FIND_KINDS in ./widget.ts, M7). */
  kind: z.string(),
  /** The place's technical name, for example `#guest-name` or `table.guests td:nth-child(2)`. */
  selector: z.string(),
  /** The page's masked path with record IDs as `:id`. */
  pagePath: z.string(),
  /** The label or column header as the widget read it, already masked. */
  label: z.string().nullable(),
  /** What the list shows: the owner's rename when there is one, else the generated description. */
  description: z.string(),
  autoDescription: z.string(),
  customDescription: z.string().nullable(),
  state: MaskingFindStateSchema,
  /** First seen within NEW_FIND_DAYS (7). */
  isNew: z.boolean(),
  firstSeenAt: IsoDateTime,
  lastSeenAt: IsoDateTime,
  seenCount: z.number().int(),
  stateChangedAt: IsoDateTime.nullable(),
  stateChangedBy: z.object({ id: Uuid, displayName: z.string() }).nullable(),
});
export type MaskingFindView = z.infer<typeof MaskingFindViewSchema>;
export const MaskingFindListResponseSchema = pagedEnvelope(MaskingFindViewSchema);
export type MaskingFindList = z.infer<typeof MaskingFindListResponseSchema>['data'];
export const MaskingFindResponseSchema = envelope(MaskingFindViewSchema);

/** A switch, a rename, or both. `description: null` clears the rename (the generated description shows again). */
export const UpdateMaskingFindBodySchema = z
  .object({
    state: MaskingFindStateSchema,
    description: z.string().trim().min(1).max(MASKING_FIND_DESCRIPTION_MAX).nullable(),
  })
  .partial()
  .refine((v) => v.state !== undefined || v.description !== undefined, { message: 'Change at least one field' });
export type UpdateMaskingFindInput = z.infer<typeof UpdateMaskingFindBodySchema>;

// ---------------------------------------------------------------------------------------------
// Environments and addresses (REQ-1.5). No audience setting (CT-026): the tab shows to anyone on a registered
// address of a switched-on app, and reporting needs a signed-in HPG account.
// ---------------------------------------------------------------------------------------------

export const CreateEnvironmentBodySchema = z.object({ code: EnvironmentCodeSchema, name: Name });
export type CreateEnvironmentInput = z.infer<typeof CreateEnvironmentBodySchema>;

/** The code can't change: the SDK passes it (REQ-1.5). */
export const UpdateEnvironmentBodySchema = z
  .object({ name: Name, sortOrder: z.number().int().min(0).max(10_000) })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field' });
export type UpdateEnvironmentInput = z.infer<typeof UpdateEnvironmentBodySchema>;

export const AddAddressBodySchema = z.object({ address: AddressInput });

// ---------------------------------------------------------------------------------------------
// Fallback owner and routing (REQ-12.3, REQ-6.6): both are Editors or Admins (poc CT-008)
// ---------------------------------------------------------------------------------------------

export const FallbackOwnerBodySchema = z.object({ personId: Uuid });

export const CreateRoutingBodySchema = z.object({ personId: Uuid, description: ShortText });
export const UpdateRoutingBodySchema = z
  .object({ personId: Uuid, description: ShortText })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field' });
export type RoutingInput = z.infer<typeof CreateRoutingBodySchema>;

// ---------------------------------------------------------------------------------------------
// The Linear target (poc CT-009, module-14 Section 2): PUT /api/apps/:appId/linear
// ---------------------------------------------------------------------------------------------

export const AppLinearBodySchema = LinearTargetInputSchema;
export type AppLinearInput = z.infer<typeof AppLinearBodySchema>;

// ---------------------------------------------------------------------------------------------
// Keys and widget state (REQ-1.2, REQ-1.4)
// ---------------------------------------------------------------------------------------------

export const WidgetStateBodySchema = z.object({ state: WidgetStateSchema });

// ---------------------------------------------------------------------------------------------
// Install (REQ-1.2, REQ-1.3, REQ-10.7) and pickers
// ---------------------------------------------------------------------------------------------

export const InstallInfoSchema = z.object({
  appKey: z.string(),
  /** `<script async src="…/widget/loader.js" data-app-key="…"></script>` */
  snippet: z.string(),
  /** The SDK steps: a copy of @feedback/sdk the team keeps in its own app, not a published package (DEP-8 B). */
  sdk: z.object({ packageName: z.string(), usage: z.string() }),
  /** The browser extension's steps; null while no extension is distributed (DEP-8 B), so none are shown. */
  extensionNote: z.string().nullable(),
  /** The Content Security Policy entries an app adds for the tool's origin. */
  csp: z.object({ scriptSrc: z.string(), connectSrc: z.string(), imgSrc: z.string() }),
  /** One per registered address: `<address>#hpg-feedback-test-capture` (REQ-10.7). */
  testCaptureLinks: z.array(z.object({ environment: z.string(), url: z.string() })),
});
export type InstallInfo = z.infer<typeof InstallInfoSchema>;
export const InstallInfoResponseSchema = envelope(InstallInfoSchema);

export const ExtensionHostSchema = z.object({
  host: z.string(),
  pathPrefix: z.string(),
  appKey: z.string(),
  /** The app's name, for the extension pop-up. Always sent; optional so a list cached before it still parses. */
  appName: z.string().optional(),
});
export type ExtensionHost = z.infer<typeof ExtensionHostSchema>;
export const ExtensionHostsResponseSchema = envelope(
  z.object({ items: z.array(ExtensionHostSchema), ttlSeconds: z.number().int().min(0) }),
);

// ---------------------------------------------------------------------------------------------
// Routing context for AI triage (M5; no HTTP route)
// ---------------------------------------------------------------------------------------------

export const RoutingContextSchema = z.object({
  apps: z.array(
    z.object({
      id: Uuid,
      name: z.string(),
      description: z.string().nullable(),
      environments: z.array(z.object({ id: Uuid, code: z.string(), name: z.string() })),
      /**
       * The app's fallback owner (an Editor or Admin), when set. With the people in `responsibilities`, these
       * are the people AI triage routes among, every one of them an Editor (poc CT-008).
       */
      owners: z.array(PersonRefSchema),
      /** What each Editor is responsible for in the app (REQ-6.6). */
      responsibilities: z.array(z.object({ person: PersonRefSchema, description: z.string() })),
      fallbackOwnerPersonId: Uuid.nullable(),
    }),
  ),
});
export type RoutingContext = z.infer<typeof RoutingContextSchema>;
